import * as React from 'react';

import { enumUnit, Unit } from '@rematter/frontend-lib-api-client';
import { Button, NumberField, Select, TextField } from '@rematter/ui-web';
import { cn } from '@rematter/ui-web/lib';
import { Trash2 } from 'lucide-react';

import { useTranslations } from '../../i18n/useTranslations';

import { SelectInventoryLot } from '~/shared/components/select-fields/SelectInventoryLot';
import { SelectInventoryTag } from '~/shared/components/select-fields/SelectInventoryTag';
import { SelectMaterial } from '~/shared/components/select-fields/SelectMaterial';

export type WorkOrderInputItemData = {
  id: string;
  existingId?: string;
  itemId: string;
  lotId?: string;
  tagId?: string;
  plannedQuantityUnit: Unit;
  plannedQuantity: string;
  notes?: string;
};

type WorkOrderInputItemRowProps = {
  item: WorkOrderInputItemData;
  onUpdate: (id: string, data: Partial<WorkOrderInputItemData>) => void;
  onRemove: (id: string) => void;
  isDisabled?: boolean;
  facilityId?: string;
  showLotSelector?: boolean;
  showTagSelector?: boolean;
};

const unitOptions = [
  { key: enumUnit.Pounds, label: 'LB' },
  { key: enumUnit.Kilograms, label: 'KG' },
  { key: enumUnit.MetricTons, label: 'MT' },
  { key: enumUnit.ShortTons, label: 'ST' },
  { key: enumUnit.Each, label: 'EA' },
];

export const WorkOrderInputItemRow = ({
  item,
  onUpdate,
  onRemove,
  isDisabled = false,
  facilityId,
  showLotSelector = false,
  showTagSelector = false,
}: WorkOrderInputItemRowProps) => {
  const t = useTranslations();

  const handleItemChange = React.useCallback(
    (itemId?: string) => {
      onUpdate(item.id, {
        itemId: itemId || '',
        lotId: undefined,
        tagId: undefined,
      });
    },
    [item.id, onUpdate],
  );

  const handleLotChange = React.useCallback(
    (lotId?: string) => {
      onUpdate(item.id, { lotId: lotId || undefined });
    },
    [item.id, onUpdate],
  );

  const handleTagChange = React.useCallback(
    (tagId?: string) => {
      onUpdate(item.id, { tagId: tagId || undefined });
    },
    [item.id, onUpdate],
  );

  const handleQuantityChange = React.useCallback(
    (value: number | null) => {
      onUpdate(item.id, { plannedQuantity: value?.toString() || '' });
    },
    [item.id, onUpdate],
  );

  const handleUnitChange = React.useCallback(
    (key: string | number) => {
      onUpdate(item.id, { plannedQuantityUnit: key as Unit });
    },
    [item.id, onUpdate],
  );

  const handleNotesChange = React.useCallback(
    (value: string) => {
      onUpdate(item.id, { notes: value });
    },
    [item.id, onUpdate],
  );

  const handleRemove = React.useCallback(() => {
    onRemove(item.id);
  }, [item.id, onRemove]);

  const getGridCols = () => {
    if (showLotSelector && showTagSelector) {
      return 'grid-cols-[1fr_1fr_1fr_120px_100px_1fr_40px]';
    }
    if (showLotSelector || showTagSelector) {
      return 'grid-cols-[1fr_1fr_120px_100px_1fr_40px]';
    }
    return 'grid-cols-[1fr_120px_100px_1fr_40px]';
  };
  const gridCols = getGridCols();

  return (
    <div
      className={cn(
        '@WorkOrderInputItemRow grid',
        gridCols,
        'items-start gap-2 rounded border border-neutral/20 p-3',
      )}
    >
      <SelectMaterial
        placeholder={t.workOrdersView.dialog.inputItems.selectItem}
        value={item.itemId || undefined}
        onChange={handleItemChange}
        isDisabled={isDisabled}
      />
      {showLotSelector && (
        <SelectInventoryLot
          placeholder={t.workOrderDetailView.completionDialog.selectLot}
          value={item.lotId || undefined}
          onChange={handleLotChange}
          isDisabled={isDisabled || !item.itemId}
          facilityId={facilityId}
        />
      )}
      {showTagSelector && (
        <SelectInventoryTag
          placeholder={t.workOrderDetailView.completionDialog.selectTag}
          value={item.tagId || undefined}
          onChange={handleTagChange}
          isDisabled={isDisabled || !item.itemId}
          facilityId={facilityId}
          itemId={item.itemId || undefined}
          lotId={item.lotId || undefined}
        />
      )}
      <NumberField
        aria-label={t.workOrdersView.dialog.inputItems.quantity}
        value={
          item.plannedQuantity ? parseFloat(item.plannedQuantity) : undefined
        }
        onChange={handleQuantityChange}
        isDisabled={isDisabled}
        minValue={0}
      />
      <Select
        placeholder={t.workOrdersView.dialog.inputItems.unit}
        value={item.plannedQuantityUnit}
        onChange={handleUnitChange}
        options={unitOptions}
        isDisabled={isDisabled}
      />
      <TextField
        placeholder={t.workOrdersView.dialog.inputItems.notes}
        value={item.notes || ''}
        onChange={handleNotesChange}
        isDisabled={isDisabled}
      />
      <Button
        variant="ghost"
        color="failure"
        icon={<Trash2 size={16} />}
        onPress={handleRemove}
        isDisabled={isDisabled}
        aria-label={t.workOrdersView.dialog.inputItems.remove}
      />
    </div>
  );
};
